import { keyframes, styled } from '@innowise-group/mui-kit';

import { DISCHARGES } from './priority-bolt.data';

// Electric blue, so the bolt is not taken for the fire: light at the top, deep at the bottom.
// The glow and the sparks are in between and read on the white and on the dark list
export const BOLT_GRADIENT = { top: '#62d0ff', bottom: '#1a6dff' };

const GLOW_COLOR = '#4aa8ff';

const IDLE_GLOW = 0.4;

const SPARK_COLOR = '#2f9dff';

// A wider pale line under a spark
const SPARK_HALO = { color: '#7cc4ff', width: 1.8, opacity: 0.35 };

// A discharge every 2.5s, the first one soon after the list opens. The sparks of a discharge start 50ms apart.
// The times are in ms and the keyframes are placed by them, so a longer interval makes the discharges rarer,
// not slower
const DISCHARGE_INTERVAL = 1500;
const FIRST_DISCHARGE = 600;
const SPARK_LAG = 50;

// Every spark fires once a cycle of all the discharges, in the turn of its own discharge
const SPARK_CYCLE = DISCHARGES.length * DISCHARGE_INTERVAL;

// The keyframe of a moment (ms) of a cycle
const at = (moment: number, cycle: number) => `${(moment * 100) / cycle}%`;

// Within 0.2s of a discharge the glow flares twice, then it stays calm till the next one
const flare = keyframes`
  0% {
    opacity: ${IDLE_GLOW};
  }
  ${at(25, DISCHARGE_INTERVAL)} {
    opacity: 1;
  }
  ${at(75, DISCHARGE_INTERVAL)} {
    opacity: 0.55;
  }
  ${at(110, DISCHARGE_INTERVAL)} {
    opacity: 0.95;
  }
  ${at(225, DISCHARGE_INTERVAL)},
  100% {
    opacity: ${IDLE_GLOW};
  }
`;

// A spark lives 0.2s: it flashes, dims, turns over into a new zigzag, flashes again
// and fades out drifting away from the bolt
const crackle = keyframes`
  0% {
    opacity: 0;
    transform: translateX(0) scaleY(1);
  }
  ${at(15, SPARK_CYCLE)} {
    opacity: 1;
  }
  ${at(60, SPARK_CYCLE)} {
    opacity: 0.3;
    transform: translateX(0.3px) scaleY(1);
  }
  ${at(65, SPARK_CYCLE)} {
    transform: translateX(0.3px) scaleY(-1);
  }
  ${at(90, SPARK_CYCLE)} {
    opacity: 1;
  }
  ${at(200, SPARK_CYCLE)},
  100% {
    opacity: 0;
    transform: translateX(0.8px) scaleY(-1);
  }
`;

export const Bolt = styled.span`
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  width: 0.75rem;
  height: 1rem;
  margin-left: 0.5rem;
`;

// The layers share the box of the bolt, the glow and the sparks spread out of it
export const Layer = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
`;

// A blurred copy of the bolt under it. With reduced motion it does not flare
export const Glow = styled(Layer)`
  fill: ${GLOW_COLOR};
  stroke: ${GLOW_COLOR};
  stroke-width: 1px;
  filter: blur(1.5px);
  opacity: ${IDLE_GLOW};
  animation: ${flare} ${DISCHARGE_INTERVAL}ms linear ${FIRST_DISCHARGE}ms infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Turns over and drifts around its start: the transforms are taken in the box of the spark
export const Spark = styled.g<{ $discharge: number; $order: number }>`
  opacity: 0;
  transform-box: fill-box;
  transform-origin: left center;
  animation: ${crackle} ${SPARK_CYCLE}ms linear infinite;
  animation-delay: ${({ $discharge, $order }) =>
    FIRST_DISCHARGE + $discharge * DISCHARGE_INTERVAL + $order * SPARK_LAG}ms;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export const SparkLine = styled.path`
  fill: none;
  stroke: ${SPARK_COLOR};
  stroke-width: 0.75px;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

export const SparkHalo = styled(SparkLine)`
  stroke: ${SPARK_HALO.color};
  stroke-width: ${SPARK_HALO.width}px;
  stroke-opacity: ${SPARK_HALO.opacity};
`;
